import type { FilterFieldRestriction, FilterRestriction } from '@portal/pkg-api/gen/admin-console'

/**
 * Conversion between the criteria rows `FilterCriteria` edits and the postfix (reverse Polish)
 * expression the dashboard API stores.
 *
 * The rows are already field restrictions on the wire — `FilterCriteria` appends
 * `{ dtype: 'F', operand1, operator, operand2Value }` — so there is no field mapping to do. What
 * the server additionally requires is that the expression reduce to exactly one value: every field
 * restriction pushes an operand and every AND pops two, so N rows serialize as those N rows
 * followed by N-1 ANDs. The builder only offers conjunction, which is what the Figma shows.
 */

const DTYPE_FIELD = 'F'
const DTYPE_LOGICAL = 'L'
const AND = 'AND'

/**
 * Row shape `FilterCriteria` manages. It appends rows with a null `operand1` until the user picks
 * a field, so the id is nullable here where the wire type requires it.
 */
export type CriteriaRow = Omit<FilterFieldRestriction, 'operand1'> & { operand1: number | null }

/** Drops the logical operators, leaving the rows the builder can edit. */
export function toCriteriaRows(restrictions: FilterRestriction[] | undefined): CriteriaRow[] {
  if (!restrictions?.length) return []
  return restrictions.filter((r): r is FilterFieldRestriction => r.dtype === DTYPE_FIELD)
}

/** Serializes rows as `[F, F, ..., AND, AND, ...]`, empty for no rows. */
export function toFilterRestrictions(rows: CriteriaRow[]): FilterRestriction[] {
  const complete = rows.filter(
    (row): row is FilterFieldRestriction => row.operand1 !== null && Boolean(row.operator),
  )
  if (complete.length === 0) return []

  const conjunctions = Array.from({ length: complete.length - 1 }, () => ({
    dtype: DTYPE_LOGICAL,
    logicalOperator: AND,
  })) as FilterRestriction[]

  return [...complete, ...conjunctions]
}
